// TP Agent Radar API client — personal shortlists (P1–P3).
//
// Own axios instance, isolated from the shared services/api.js (small PR surface,
// no risk to the shared client) — mirrors services/scoutRequestService.js and
// services/scoutAgentService.js. Paths are the approved SCHEMA-PLAN contract
// (dashboard/agent_radar_views.py, router prefix `agent/radar/`), under the
// dashboard app mount (agent-side scout endpoints sit at /dashboard/scout-…):
//   lists            → /dashboard/agent/radar/lists/
//   entries          → /dashboard/agent/radar/entries/[{id}/][move|copy|labels|notes]
//   labels           → /dashboard/agent/radar/labels/
//   profile-status   → /dashboard/agent/radar/profile-status/   (P2)
//   save-from-profile→ /dashboard/agent/radar/save-from-profile/ (P2)
//   share            → /dashboard/agent/radar/share/            (P3)
//   snapshots        → /dashboard/agent/radar/snapshots/{id}/   (P3)
// Everything owner-scoped server-side.
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_ENDPOINT;

const authApi = axios.create({ baseURL: API_BASE_URL, withCredentials: true });

// Same Bearer-token attachment the scout / club-network / agent-tender clients use.
authApi.interceptors.request.use((config) => {
  try {
    const tokens = JSON.parse(localStorage.getItem("tokens") || "null");
    if (tokens?.access) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${tokens.access}`;
    }
  } catch (e) {
    /* no token — request proceeds and the server returns 401 */
  }
  return config;
});

const fail = (error) => ({
  success: false,
  status: error?.response?.status,
  error:
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    (typeof error?.response?.data === "object"
      ? Object.values(error.response.data)[0]
      : null) ||
    "Something went wrong. Please try again.",
});

const isCancel = (error) => error?.name === "CanceledError" || error?.name === "AbortError";

// List endpoints may return either a bare array or a DRF paginated envelope.
const asList = (data) => (Array.isArray(data) ? data : data?.results || []);

const BASE = "/dashboard/agent/radar";

export const agentRadarService = {
  // ── Lists ──────────────────────────────────────────────────────────────────
  getLists: async (signal) => {
    try {
      const res = await authApi.get(`${BASE}/lists/`, { signal });
      return { success: true, data: asList(res.data) };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  createList: async (name) => {
    try {
      const res = await authApi.post(`${BASE}/lists/`, { name });
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  renameList: async (id, name) => {
    try {
      const res = await authApi.patch(`${BASE}/lists/${id}/`, { name });
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // Soft-delete a custom list; the server moves its entries to My Radar.
  deleteList: async (id) => {
    try {
      const res = await authApi.delete(`${BASE}/lists/${id}/`);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // ── Entries ─────────────────────────────────────────────────────────────────
  // params: { list, position, label, sort }  (sort = recent | name | contract)
  getEntries: async (params, signal) => {
    try {
      const res = await authApi.get(`${BASE}/entries/`, { params, signal });
      return { success: true, data: asList(res.data) };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  getEntry: async (id, signal) => {
    try {
      const res = await authApi.get(`${BASE}/entries/${id}/`, { signal });
      return { success: true, data: res.data };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  // Soft "Remove from your radar".
  removeEntry: async (id) => {
    try {
      const res = await authApi.delete(`${BASE}/entries/${id}/`);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // Move OUT of `fromListId` (the currently viewed list) INTO `listId`.
  moveEntry: async (id, listId, fromListId) => {
    try {
      const body = { list_id: listId };
      if (fromListId) body.from_list_id = fromListId;
      const res = await authApi.post(`${BASE}/entries/${id}/move/`, body);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  copyEntry: async (id, listId) => {
    try {
      const res = await authApi.post(`${BASE}/entries/${id}/copy/`, { list_id: listId });
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  assignLabel: async (id, labelId) => {
    try {
      const res = await authApi.post(`${BASE}/entries/${id}/labels/`, { label_id: labelId });
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  unassignLabel: async (id, labelId) => {
    try {
      const res = await authApi.delete(`${BASE}/entries/${id}/labels/`, { data: { label_id: labelId } });
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  getNotes: async (id, signal) => {
    try {
      const res = await authApi.get(`${BASE}/entries/${id}/notes/`, { signal });
      return { success: true, data: asList(res.data) };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  addNote: async (id, text) => {
    try {
      const res = await authApi.post(`${BASE}/entries/${id}/notes/`, { text });
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // ── Labels (owner's palette) ────────────────────────────────────────────────
  getLabels: async (signal) => {
    try {
      const res = await authApi.get(`${BASE}/labels/`, { signal });
      return { success: true, data: asList(res.data) };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  createLabel: async ({ name, color }) => {
    try {
      const res = await authApi.post(`${BASE}/labels/`, { name, color });
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // ── Profile header button + picker (P2) ─────────────────────────────────────
  // params: { ws_player_id } or { player_id } → { on_radar, list_ids, lists }
  getProfileStatus: async (params, signal) => {
    try {
      const res = await authApi.get(`${BASE}/profile-status/`, { params, signal });
      return { success: true, data: res.data };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  // Idempotent upsert of the entry + its memberships to the final set of list ids.
  // Empty listIds removes the subject from the radar. camelCase args → snake_case
  // body. tsCoachId (Coach-Radar Phase 1) keys a coach entry — exactly one key.
  saveFromProfile: async ({ wsPlayerId, playerId, tsCoachId, listIds }) => {
    try {
      const body = { list_ids: listIds };
      if (wsPlayerId != null) body.ws_player_id = wsPlayerId;
      if (playerId != null) body.player_id = playerId;
      if (tsCoachId != null) body.ts_coach_id = tsCoachId;
      const res = await authApi.post(`${BASE}/save-from-profile/`, body);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // ── Share to chat + snapshots (P3) ──────────────────────────────────────────
  // { type: 'player'|'list', entry_id|list_id, channel_ids: [], message? }
  share: async (payload) => {
    try {
      const res = await authApi.post(`${BASE}/share/`, payload);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  getSnapshot: async (id, signal) => {
    try {
      const res = await authApi.get(`${BASE}/snapshots/${id}/`, { signal });
      return { success: true, data: res.data };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  // Copy a shared snapshot's players into the viewer's own radar lists (P3,
  // frame 2b). Server responds { saved_count, skipped_count } (skipped = players
  // already on the target lists). Owner-scoped + agent-only server-side.
  saveSnapshotToRadar: async (id, listIds) => {
    try {
      const res = await authApi.post(`${BASE}/snapshots/${id}/save-to-radar/`, {
        list_ids: listIds,
      });
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },
};

export default agentRadarService;
